import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import { useTheme } from '@mui/material/styles';
import {
  IconCheck,
  IconChevronUp,
  IconTestPipe,
  IconTrophy,
} from '@tabler/icons-react';

import { Typography } from '@zendesk/forethought-elements';
import AbTestDetailSection from './AbTestDetailSection';
import ABTestSidebar from './ABTestSidebar';
import AgentVariantsSection from './AgentVariantsSection';
import {
  getIsAgentsAndVariantsSectionComplete,
  getIsDetailSectionComplete,
  getIsWinnerSectionComplete,
  hasValidVariants as getHasValidVariants,
} from './utils';
import WinnerTestEndSection from './WinnerTestEndSection';
import ExperimentsIcon from 'src/assets/images/experiments.svg';
import Spinner from 'src/components/spinner';
import { useGetAbTestConfigByIdQuery } from 'src/services/agent-ab-test/agentAbTestApi';

const AbTestConfigPage = () => {
  const { t } = useTranslation('agent-ab-test');
  const { palette } = useTheme();
  const { abTestId = '' } = useParams();

  const { data, isLoading } = useGetAbTestConfigByIdQuery(abTestId, {
    skip: !abTestId,
  });

  if (isLoading || !data) {
    return <Spinner />;
  }

  const showSidebar = getHasValidVariants(data);

  return (
    <Box
      sx={{
        bgcolor: palette.colors.white,
        height: '100%',
        overflow: 'auto',
        paddingTop: '34px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '119px',
          justifyContent: 'center',
          margin: '0 auto',
          maxWidth: showSidebar ? '1300px' : '800px',
          padding: '0 40px',
        }}
      >
        {/* Main content column */}
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            gap: '25px',
            maxWidth: '700px',
            width: '100%',
          }}
        >
          {data.status !== 'draft' && data.summary && (
            <Box
              sx={{
                background: `linear-gradient(135deg, ${palette.colors.blue[100]}, ${palette.colors.slate[100]})`,
                borderRadius: '8px',
                padding: '20px 24px',
              }}
            >
              <Typography variant='font14'>{data.summary}</Typography>
            </Box>
          )}
          <AbTestSectionContainer
            defaultExpanded
            description={t('configPage.step1.description')}
            icon={
              <IconTestPipe color={palette.colors.teal[500]} width='20px' />
            }
            isCompleted={getIsDetailSectionComplete(data)}
            stepNumber={1}
            title={t('configPage.step1.title')}
          >
            <AbTestDetailSection />
          </AbTestSectionContainer>
          <AbTestSectionContainer
            description={t('configPage.step2.description')}
            icon={<img src={ExperimentsIcon} width='20px' />}
            isCompleted={getIsAgentsAndVariantsSectionComplete(data)}
            stepNumber={2}
            title={t('configPage.step2.title')}
          >
            <AgentVariantsSection />
          </AbTestSectionContainer>
          <AbTestSectionContainer
            description={t('configPage.step3.description')}
            icon={<IconTrophy color={palette.chart[7]} width='20px' />}
            isCompleted={getIsWinnerSectionComplete(data)}
            stepNumber={3}
            title={t('configPage.step3.title')}
          >
            <WinnerTestEndSection />
          </AbTestSectionContainer>
        </Box>

        {/* Sidebar */}
        {showSidebar && <ABTestSidebar config={data} />}
      </Box>
    </Box>
  );
};

export default AbTestConfigPage;

interface AbTestSectionContainerProps {
  children: React.ReactNode;
  defaultExpanded?: boolean;
  description: string;
  icon?: React.ReactNode;
  isCompleted: boolean;
  stepNumber: number;
  title: string;
}

export const AbTestSectionContainer = ({
  children,
  defaultExpanded = false,
  description,
  icon,
  isCompleted,
  stepNumber,
  title,
}: AbTestSectionContainerProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const { palette } = useTheme();

  const handleToggle = () => {
    setIsExpanded(prev => !prev);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '33px',
        width: '100%',
      }}
    >
      {/* Step number circle */}
      <Box
        sx={{
          alignItems: 'center',
          border: `2px solid ${
            isCompleted ? palette.colors.green[500] : palette.colors.slate[300]
          }`,
          borderRadius: '50%',
          color: isCompleted
            ? palette.colors.green[500]
            : palette.colors.slate[300],
          display: 'flex',
          flexShrink: 0,
          height: '32px',
          justifyContent: 'center',
          width: '32px',
        }}
      >
        {isCompleted ? (
          <IconCheck size={18} />
        ) : (
          <Typography color={palette.colors.slate[300]} variant='font18Bold'>
            {stepNumber}
          </Typography>
        )}
      </Box>
      {/* Content container */}
      <Box
        sx={{
          borderBottom: `1px solid ${palette.colors.slate[200]}`,
          flex: 1,
          maxWidth: '635px',
          minWidth: 0,
          paddingBottom: '25px',
        }}
      >
        {/* Header - clickable to toggle */}
        <Box
          onClick={handleToggle}
          sx={{
            alignItems: 'flex-start',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            userSelect: 'none',
          }}
        >
          <Box>
            {/* Title with icon */}
            <Box sx={{ alignItems: 'center', display: 'flex', gap: '8px' }}>
              {icon && (
                <Box
                  sx={{
                    color: palette.colors.purple[500],
                    display: 'flex',
                  }}
                >
                  {icon}
                </Box>
              )}
              <Typography variant='font18Bold'>{title}</Typography>
            </Box>
            {/* Description */}
            <Typography color={palette.colors.grey[600]} variant='font12'>
              {description}
            </Typography>
          </Box>

          {/* Chevron icon */}
          <Box
            sx={{
              '&:hover': {
                backgroundColor: palette.colors.teal[100],
                cursor: 'pointer',
              },
              alignItems: 'center',
              borderRadius: '50%',
              color: palette.colors.teal[700],
              display: 'flex',
              height: 32,
              justifyContent: 'center',
              transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 0.2s ease-in-out',
              width: 32,
            }}
          >
            <IconChevronUp size={20} />
          </Box>
        </Box>

        {/* Collapsible content */}
        <Collapse in={isExpanded}>
          <Box sx={{ pt: '16px' }}>{children}</Box>
        </Collapse>
      </Box>
    </Box>
  );
};
